// TabMemory: persisted record of a saved tab state.
// Matches the JSON shape returned by the Go `tab_memory` handler. The
// raw payload can be in either snake_case or camelCase; `normalizeTabMemory`
// collapses both into the camelCase form the rest of the app uses.

export type TabMemoryPriority = 'low' | 'medium' | 'high';

export type TabMemoryGroup = {
  id: string;
  name: string;
  color: string;
  tabCount?: number;
  createdAt?: string;
};

export interface TabMemory {
  id: string;
  url: string;
  title: string;
  favicon?: string;
  windowId?: string;
  groupId?: string | null;
  tags: string[];
  priority: TabMemoryPriority;
  pinned: boolean;
  scrollPosition?: number;
  accessCount: number;
  content?: string;
  notes?: string;
  visitedAt: string;
  lastAccessedAt?: string;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
}

export interface TabMemoryFilters {
  tag?: string;
  pinned?: boolean;
  groupId?: string;
  q?: string;
}

const PRIORITIES: TabMemoryPriority[] = ['low', 'medium', 'high'];

function asString(v: unknown, fallback = ''): string {
  if (v == null) return fallback;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return fallback;
}

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function asBool(v: unknown, fallback = false): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true' || v === 1) return true;
  if (v === 'false' || v === 0) return false;
  return fallback;
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

function asPriority(v: unknown): TabMemoryPriority {
  const lower = asString(v, 'medium').toLowerCase();
  return (PRIORITIES as string[]).includes(lower) ? (lower as TabMemoryPriority) : 'medium';
}

function pickId(...candidates: unknown[]): string {
  for (const c of candidates) {
    const s = asString(c, '');
    if (s) return s;
  }
  return '';
}

function pickTimestamp(...candidates: unknown[]): string {
  for (const c of candidates) {
    const s = asString(c, '');
    if (s) return s;
  }
  return new Date().toISOString();
}

function pickMetadata(raw: Record<string, unknown>): Record<string, unknown> {
  const m = raw.metadata;
  if (m && typeof m === 'object' && !Array.isArray(m)) {
    return m as Record<string, unknown>;
  }
  return {};
}

function pickNotes(raw: Record<string, unknown>, metadata: Record<string, unknown>): string | undefined {
  if (typeof raw.notes === 'string') return raw.notes;
  const fromMetadata = metadata.notes;
  if (typeof fromMetadata === 'string') return fromMetadata;
  return undefined;
}

export function normalizeTabMemory(raw: Record<string, unknown>): TabMemory {
  const metadata = pickMetadata(raw);
  const tags = asStringArray(raw.tags);
  const visitedAt = pickTimestamp(raw.visitedAt, raw.visited_at);
  const updatedAt = pickTimestamp(raw.updatedAt, raw.updated_at, visitedAt);
  const createdAt = pickTimestamp(raw.createdAt, raw.created_at, visitedAt);
  const lastAccessedAt = pickTimestamp(
    raw.lastAccessedAt,
    raw.last_accessed_at,
    raw.updatedAt,
    raw.updated_at,
    visitedAt,
  );

  return {
    id: pickId(raw.id),
    url: asString(raw.url, asString(raw.content, '')),
    title: asString(raw.title, 'Untitled'),
    favicon: asString(raw.favicon, '') || undefined,
    windowId: asString(raw.windowId ?? raw.window_id, '') || undefined,
    groupId:
      (raw.groupId as string | null | undefined) ??
      (raw.group_id as string | null | undefined) ??
      null,
    tags,
    priority: asPriority(raw.priority),
    pinned: asBool(raw.pinned),
    scrollPosition: raw.scrollPosition != null ? asNumber(raw.scrollPosition) : raw.scroll_position != null ? asNumber(raw.scroll_position) : undefined,
    accessCount: asNumber(raw.accessCount ?? raw.access_count),
    content: asString(raw.content, '') || undefined,
    notes: pickNotes(raw, metadata),
    visitedAt,
    lastAccessedAt,
    createdAt,
    updatedAt,
    metadata,
  };
}

export function normalizeTabMemoryList(payload: unknown): TabMemory[] {
  let list: unknown[] = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.memories)) list = obj.memories;
    else if (Array.isArray(obj.results)) list = obj.results;
  }
  return list
    .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
    .map(normalizeTabMemory);
}

// Build a TabMemory from a partial payload the page may construct
// (e.g. when seeding a new card from a manually-typed URL). Useful
// for optimistic UI updates.
export function tabMemoryFromDraft(draft: Partial<TabMemory>): TabMemory {
  const now = new Date().toISOString();
  return {
    id: draft.id ?? `mem-${Date.now()}`,
    url: draft.url ?? '',
    title: draft.title ?? draft.url ?? 'Untitled',
    favicon: draft.favicon,
    windowId: draft.windowId,
    groupId: draft.groupId ?? null,
    tags: draft.tags ?? [],
    priority: draft.priority ?? 'medium',
    pinned: draft.pinned ?? false,
    scrollPosition: draft.scrollPosition,
    accessCount: draft.accessCount ?? 0,
    content: draft.content,
    notes: draft.notes,
    visitedAt: draft.visitedAt ?? now,
    lastAccessedAt: draft.lastAccessedAt ?? now,
    createdAt: draft.createdAt ?? now,
    updatedAt: draft.updatedAt ?? now,
    metadata: draft.metadata ?? {},
  };
}

export function tagColor(tag: string): string {
  // Stable hash → palette index so the same tag is the same color everywhere.
  const palette = [
    'from-rose-500 to-pink-500',
    'from-violet-500 to-purple-500',
    'from-blue-500 to-cyan-500',
    'from-emerald-500 to-teal-500',
    'from-amber-500 to-orange-500',
  ];
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}
